import { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Skeleton } from '../Skeleton';
import {
    listInsights,
    recordInsightFeedback,
    type Insight,
} from '../../mentorshipService';
import './RecommendationsList.css';
import './mentorship.css';

type Props = {
    userId: number;
};

const PRIORITY_ORDER: Record<Insight['priority'], number> = {
    high: 0,
    medium: 1,
    low: 2,
};

function RecommendationsList({ userId }: Props) {
    const [insights, setInsights] = useState<Insight[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [feedbackInFlight, setFeedbackInFlight] = useState<Set<string>>(new Set());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const page = await listInsights({ user_id: userId });
                if (!cancelled) {
                    const sorted = [...page.results].sort(
                        (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
                    );
                    setInsights(sorted);
                }
            } catch (err) {
                console.error('[RecommendationsList] failed to load insights', err);
                if (!cancelled) setInsights([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId]);

    const handleFeedback = async (
        insightId: string,
        action: 'accepted' | 'dismissed',
    ) => {
        // Optimistic update: bump the local counter, then call the server.
        // On failure, roll back.
        setFeedbackInFlight((prev) => new Set(prev).add(insightId));
        const original = insights ?? [];
        const updated = original.map((i) =>
            i.id === insightId
                ? {
                      ...i,
                      accepted_count:
                          action === 'accepted' ? i.accepted_count + 1 : i.accepted_count,
                      dismissed_count:
                          action === 'dismissed'
                              ? i.dismissed_count + 1
                              : i.dismissed_count,
                  }
                : i,
        );
        setInsights(updated);

        try {
            await recordInsightFeedback(insightId, action);
        } catch (err) {
            console.error('[RecommendationsList] feedback failed; rolling back', err);
            setInsights(original);
        } finally {
            setFeedbackInFlight((prev) => {
                const next = new Set(prev);
                next.delete(insightId);
                return next;
            });
        }
    };

    if (loading) {
        return (
            <div className="recommendations-list__loading" aria-busy="true">
                <Skeleton width="100%" height="64px" borderRadius="12px" />
                <Skeleton width="100%" height="64px" borderRadius="12px" />
                <Skeleton width="100%" height="64px" borderRadius="12px" />
            </div>
        );
    }

    if (!insights || insights.length === 0) {
        return (
            <div className="recommendations-list__empty">
                <Lightbulb size={32} />
                <h3>No recommendations yet</h3>
                <p>
                    Once your mentor finalizes a report, personalized recommendations
                    will appear here.
                </p>
            </div>
        );
    }

    return (
        <ul className="recommendations-list">
            {insights.map((insight) => {
                const inFlight = feedbackInFlight.has(insight.id);
                const priorityClass = `mentorship-chip mentorship-chip--${
                    insight.priority === 'high' ? 'sla-breach' : insight.priority === 'medium' ? 'sla-warn' : 'sla-ok'
                }`;
                return (
                    <li key={insight.id} className="recommendations-list__item">
                        <Card variant="row">
                            <Card.Header>
                                <Lightbulb size={20} />
                                <div className="recommendations-list__body">
                                    <div className="recommendations-list__meta">
                                        <span className={priorityClass}>
                                            {insight.priority}
                                        </span>
                                        <span className="recommendations-list__type">
                                            {insight.type}
                                        </span>
                                    </div>
                                    <p className="recommendations-list__prompt">
                                        {insight.prompt}
                                    </p>
                                    {insight.trigger_context && (
                                        <p className="recommendations-list__context">
                                            Context: {insight.trigger_context}
                                        </p>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Footer>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<ThumbsUp size={16} />}
                                    onClick={() => handleFeedback(insight.id, 'accepted')}
                                    disabled={inFlight}
                                    aria-label="Mark this recommendation as helpful"
                                >
                                    Helpful · {insight.accepted_count}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={<ThumbsDown size={16} />}
                                    onClick={() => handleFeedback(insight.id, 'dismissed')}
                                    disabled={inFlight}
                                    aria-label="Dismiss this recommendation"
                                >
                                    Dismiss · {insight.dismissed_count}
                                </Button>
                            </Card.Footer>
                        </Card>
                    </li>
                );
            })}
        </ul>
    );
}

export default RecommendationsList;
